import React from 'react';
import styles from './style.module.scss';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import type { ClientPreferences } from 'data/client-info-pages';

export interface ClientPreferencesProps {
  /** Client preferences */
  preferences: ClientPreferences;
  /** Client name for personalization */
  clientName: string;
}

interface PreferenceItemProps {
  icon: React.ReactNode;
  label: string;
  value: string | number | boolean | undefined;
  isBoolean?: boolean;
}

const PreferenceItem = ({ icon, label, value, isBoolean }: PreferenceItemProps) => {
  const { t } = useTranslation(Namespaces.COMMON);

  if (value === undefined || value === null) return null;

  const displayValue = isBoolean
    ? (value
      ? t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_YES, { defaultValue: 'Yes' })
      : t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_NO, { defaultValue: 'No' }))
    : typeof value === 'number'
      ? value.toLocaleString()
      : value;

  return (
    <div className={styles.preferenceItem}>
      <span className={styles.preferenceIcon}>{icon}</span>
      <div className={styles.preferenceContent}>
        <span className={styles.preferenceLabel}>{label}</span>
        <span className={styles.preferenceValue}>{displayValue}</span>
      </div>
    </div>
  );
};

/**
 * Displays client preferences for personalized info pages.
 */
const ClientPreferencesDisplay = ({ preferences, clientName }: ClientPreferencesProps) => {
  const { formatCurrency } = useI18nFormatting();
  const { t } = useTranslation(Namespaces.COMMON);

  const hasPreferences = Object.values(preferences).some(
    v => v !== undefined && v !== null && (Array.isArray(v) ? v.length > 0 : true)
  );

  if (!hasPreferences) return null;

  const budgetDisplay = preferences.budget_min || preferences.budget_max
    ? `${formatCurrency(preferences.budget_min || 0)} - ${formatCurrency(preferences.budget_max || 0)}`
    : undefined;

  const propertyTypesDisplay = preferences.property_types?.length
    ? preferences.property_types.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')
    : undefined;

  const walkabilityKeys: Record<number, { key: string; defaultValue: string }> = {
    1: { key: LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_IMPORTANCENOTIMPORTANT, defaultValue: 'Not Important' },
    2: { key: LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_IMPORTANCESOMEWHATIMPORTANT, defaultValue: 'Somewhat Important' },
    3: { key: LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_IMPORTANCEMODERATE, defaultValue: 'Moderate' },
    4: { key: LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_IMPORTANCEIMPORTANT, defaultValue: 'Important' },
    5: { key: LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_IMPORTANCEVERYIMPORTANT, defaultValue: 'Very Important' },
  };

  return (
    <section className={styles.component}>
      <h2 className={styles.heading}>
        {t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_HEADING, {
          name: clientName.split(' ')[0].replace(/\b\w/g, c => c.toUpperCase()),
          defaultValue: 'What {{name}} is looking for',
        })}
      </h2>
      <p className={styles.description}>
        {t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_DESCRIPTION, { defaultValue: 'This page is personalized based on specific preferences' })}
      </p>

      <div className={styles.preferencesGrid}>
        {/* Budget */}
        {budgetDisplay && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_BUDGETRANGELABEL, { defaultValue: 'Budget Range' })}
            value={budgetDisplay}
          />
        )}

        {/* Property Types */}
        {propertyTypesDisplay && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_PROPERTYTYPESLABEL, { defaultValue: 'Property Types' })}
            value={propertyTypesDisplay}
          />
        )}

        {/* Transportation */}
        {preferences.drives !== undefined && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_DRIVESLABEL, { defaultValue: 'Drives' })}
            value={preferences.drives}
            isBoolean
          />
        )}

        {/* Walkability */}
        {preferences.walkability_preference && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
                <path d="M10 8h4l2 10-4 4-4-4z" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_WALKABILITYLABEL, { defaultValue: 'Walkability' })}
            value={t(
              walkabilityKeys[preferences.walkability_preference].key,
              { defaultValue: walkabilityKeys[preferences.walkability_preference].defaultValue }
            )}
          />
        )}

        {/* Family */}
        {preferences.has_children !== undefined && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_HASCHILDRENLABEL, { defaultValue: 'Has Children' })}
            value={preferences.has_children}
            isBoolean
          />
        )}

        {/* Children Ages */}
        {preferences.children_ages?.length > 0 && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="5" />
                <path d="M20 21a8 8 0 1 0-16 0" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_CHILDRENAGESLABEL, { defaultValue: 'Children Ages' })}
            value={preferences.children_ages.join(', ')}
          />
        )}

        {/* School Priority */}
        {preferences.school_district_priority && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_SCHOOLDISTRICTPRIORITYLABEL, { defaultValue: 'School District Priority' })}
            value={preferences.school_district_priority}
            isBoolean
          />
        )}

        {/* Bedrooms */}
        {preferences.min_bedrooms && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7v11a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7" />
                <path d="M1 7h22" />
                <path d="M5 7V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_MINBEDROOMSLABEL, { defaultValue: 'Min Bedrooms' })}
            value={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_MINBEDROOMSVALUE, { count: preferences.min_bedrooms, defaultValue: '{{count}}+' })}
          />
        )}

        {/* Bathrooms */}
        {preferences.min_bathrooms && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 12h16a2 2 0 0 1 2 2v2a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4v-2a2 2 0 0 1 2-2z" />
                <path d="M6 12V6a2 2 0 0 1 2-2h.93a2 2 0 0 1 1.66.9l.38.57a2 2 0 0 0 1.66.9H18v6" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_MINBATHROOMSLABEL, { defaultValue: 'Min Bathrooms' })}
            value={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_MINBATHROOMSVALUE, { count: preferences.min_bathrooms, defaultValue: '{{count}}+' })}
          />
        )}

        {/* Commute */}
        {preferences.max_commute_minutes && (
          <PreferenceItem
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            }
            label={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_MAXCOMMUTELABEL, { defaultValue: 'Max Commute' })}
            value={t(LOCALE_COMMON_PATHS.CITYINFO_CLIENTPREFERENCES_MAXCOMMUTEVALUE, { minutes: String(preferences.max_commute_minutes), defaultValue: '{{minutes}} minutes' })}
          />
        )}
      </div>
    </section>
  );
};

export default ClientPreferencesDisplay;
